import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "../../components/DailyBudgetInvoiceItemsTable/aggridCustom.css";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { Alert, Box, Button, Snackbar, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import useUpdateAuthToken from "../../hooks/useUpdateAuthToken";
import { useGetAlegraItemsQuery } from "../../services/alegraItems/alegraItems";
import { AlegraItemType } from "../../types/types";
import { RootState } from "../../app/store";
import { PageLayout } from "../../components/PageLayout/PageLayout";
import { PageContentBox } from "../shared/styledComponents";
import { ALEGRA_ITEMS_TEXTS } from "../shared/constants";
import { formatCOP } from "../../utils/format";

const TABLE_HEIGHT = "700px";
// Alegra's /items endpoint hard-caps `limit` at 30 (400s above that) — see
// docs/django-retirement-migration/migration-plan.md §9.
const PAGE_SIZE = 30;

const AlegraItemsPage: React.FC = () => {
  useUpdateAuthToken();

  // GET /alegra-items requires a bearer token (unlike every other read endpoint, which is
  // public) — the query must wait for useUpdateAuthToken's async fetch to land the token in
  // Redux, otherwise the first request goes out unauthenticated and gets a 401.
  const { token, isLoading: isAuthLoading } = useSelector((state: RootState) => state.auth);
  const [page, setPage] = useState(0);

  const { data, isFetching, isError } = useGetAlegraItemsQuery(
    { start: page * PAGE_SIZE, limit: PAGE_SIZE },
    { skip: isAuthLoading || !token },
  );

  const rowData = useMemo(() => (isFetching || !data ? undefined : data.items), [isFetching, data]);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  const columnDefs: ColDef<AlegraItemType>[] = useMemo(
    () => [
      { headerName: ALEGRA_ITEMS_TEXTS.colId, field: "id", filter: true, width: 100 },
      {
        headerName: ALEGRA_ITEMS_TEXTS.colReference,
        field: "reference",
        filter: true,
        width: 150,
      },
      { headerName: ALEGRA_ITEMS_TEXTS.colName, field: "name", filter: true, flex: 1 },
      {
        headerName: ALEGRA_ITEMS_TEXTS.colDescription,
        field: "description",
        filter: true,
        flex: 1,
      },
      { headerName: ALEGRA_ITEMS_TEXTS.colStatus, field: "status", width: 130, filter: true },
      {
        headerName: ALEGRA_ITEMS_TEXTS.colPrice,
        width: 150,
        valueGetter: (params) => params.data?.price?.[0]?.price,
        valueFormatter: (params) => (params.value != null ? formatCOP(Number(params.value)) : ""),
      },
      {
        headerName: ALEGRA_ITEMS_TEXTS.colUnit,
        width: 120,
        valueGetter: (params) => params.data?.inventory?.unit ?? "",
      },
    ],
    [],
  );

  return (
    <PageLayout fullWidth showSearch={false}>
      <PageContentBox>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            {ALEGRA_ITEMS_TEXTS.pageTitle}
          </Typography>
          {data && (
            <Typography variant="body2" color="text.secondary">
              {ALEGRA_ITEMS_TEXTS.totalItems(data.total)}
            </Typography>
          )}
        </Box>

        <div className="ag-theme-quartz" style={{ width: "100%", height: TABLE_HEIGHT }}>
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            overlayLoadingTemplate={`<span>${ALEGRA_ITEMS_TEXTS.loading}</span>`}
            overlayNoRowsTemplate={`<span>${ALEGRA_ITEMS_TEXTS.noRows}</span>`}
          />
        </div>

        <Box display="flex" justifyContent="center" alignItems="center" gap={2} mt={2}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ChevronLeftIcon />}
            disabled={page === 0 || isFetching}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            {ALEGRA_ITEMS_TEXTS.prevPage}
          </Button>
          <Typography variant="body2">
            {ALEGRA_ITEMS_TEXTS.pageIndicator(page + 1, totalPages)}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            endIcon={<ChevronRightIcon />}
            disabled={page + 1 >= totalPages || isFetching}
            onClick={() => setPage((p) => p + 1)}
          >
            {ALEGRA_ITEMS_TEXTS.nextPage}
          </Button>
        </Box>
      </PageContentBox>

      <Snackbar open={isError}>
        <Alert severity="error">{ALEGRA_ITEMS_TEXTS.loadError}</Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default AlegraItemsPage;
